import React from 'react';
import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { NavContext } from '../context/NavContext';
import Logo from '../assets/logo-badge.svg?react';
import {
  useGoldUnderline,
  GoldUnderlineSpan,
  GOLD,
  CHARCOAL,
} from '../hooks/useGoldUnderline';

gsap.registerPlugin(ScrollTrigger);

const GOLD_LIGHT = '#d4b87a';
const CREAM = '#e8e2d9';

const NAV_LINKS = [
  { label: 'Portfolio', to: '/portfolio' },
  { label: 'Testimonials', to: '/testimonials' },
  { label: 'About', to: '/about' },
];

const SOCIALS = [
  { label: 'Instagram', href: 'https://www.instagram.com/boydenrenovations/' },
  { label: 'Facebook', href: 'https://www.facebook.com/boydenrenovations/' },
];

function NavLink({ label, to }: { label: string; to: string }) {
  const { elRef, lineRef } = useGoldUnderline<HTMLAnchorElement>();

  return (
    <a
      href={to}
      ref={elRef}
      className="relative inline-flex text-h4 items-center no-underline"
      style={{ color: CHARCOAL, letterSpacing: '0.02em' }}
    >
      {label}
      <GoldUnderlineSpan lineRef={lineRef} />
    </a>
  );
}

const ScrollToTopButton = () => {
  const { elRef, lineRef } = useGoldUnderline<HTMLButtonElement>();

  return (
    <button
      ref={elRef}
      type="button"
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className="relative w-auto uppercase tracking-[4px] font-extralight font-sans py-3"
      style={{ color: CHARCOAL }}
    >
      Scroll to Top
      <GoldUnderlineSpan lineRef={lineRef} />
    </button>
  );
};

function SocialLink({ label, href }: { label: string; href: string }) {
  const { elRef, lineRef } = useGoldUnderline<HTMLAnchorElement>();

  return (
    <a
      href={href}
      ref={elRef}
      target="_blank"
      rel="noopener noreferrer"
      className="relative no-underline uppercase text-h5"
      style={{ color: CHARCOAL, letterSpacing: '0.12em' }}
    >
      {label}
      <GoldUnderlineSpan lineRef={lineRef} />
    </a>
  );
}

export function Footer({ isLoaded }: { isLoaded: boolean }) {
  const year = new Date().getFullYear();
  const footerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!isLoaded) return;
    const ctx = gsap.context(() => {
      const trigger = footerRef.current;

      const tl = gsap.timeline({
        scrollTrigger: { trigger, start: 'top 90%', once: true },
      });

      tl.from('.ft-divider', {
        scaleX: 0,
        transformOrigin: 'left',
        duration: 0.75,
        ease: 'power3.inOut',
      });
      tl.from(
        '.ft-orn-line',
        { scaleX: 0, duration: 0.75, ease: 'power3.inOut', stagger: 0.1 },
        '-=0.3'
      );
      tl.from(
        '.ft-orn-diamond',
        { opacity: 0, duration: 0.5, ease: 'back.out(2.5)' },
        '-=0.4'
      );
      tl.from(
        '.ft-col',
        { y: 40, opacity: 0, duration: 0.9, ease: 'power3.out', stagger: 0.13 },
        '-=0.5'
      );
      tl.from(
        '.ft-bottom',
        { y: 18, opacity: 0, duration: 0.7, ease: 'power2.out' },
        '-=0.3'
      );
    }, footerRef);

    return () => ctx.revert();
  }, [isLoaded]);

  const { setHideNav } = React.useContext(NavContext);

  const footerIntersectRef = React.useCallback(
    (node: Element | null) => {
      if (node !== null) {
        const observer = new IntersectionObserver(
          ([entry]) => {
            // Log this to see if mobile is actually hitting the threshold
            setHideNav(entry.isIntersecting);
          },
          {
            threshold: 0.8, // Lowering this to 0.5 is safer for mobile
            rootMargin: '0px 0px -50px 0px', // Triggers 50px before it hits the bottom
          }
        );

        observer.observe(node);
      }
    },
    [setHideNav]
  );

  return (
    <footer
      ref={footerRef}
      className="w-full flex min-h-screen flex-col" // added justify-between
      style={{ background: CREAM, color: CHARCOAL }}
      data-nav-theme="light"
    >
      <div
        ref={footerIntersectRef}
        className="max-w-7xl w-full mx-auto flex min-h-screen flex-col justify-between font-light" // added justify-between
      >
        {/* ── Horizontal Divider ── */}
        <div
          className="ft-divider mx-5 h-px origin-left sm:mx-8 lg:mx-20"
          style={{ background: GOLD_LIGHT }}
        />

        {/* ── Main Grid ── */}
        <div className="flex flex-1 flex-col gap-5 px-5 sm:grid sm:grid-cols-1 sm:gap-12 sm:px-8 lg:grid lg:grid-cols-2 lg:gap-0 lg:px-20 lg:pb-24 lg:pt-24">
          {/* Brand */}
          <div className="ft-col flex flex-1 flex-col justify-between lg:pr-20">
            <span
              className="m-0 mb-5 flex justify-center font-normal uppercase"
              style={{
                fontSize: 'clamp(1.3rem, 5vw, 2rem)',
                color: GOLD,
                letterSpacing: '0.18em',
              }}
            >
              <Logo
                className="w-48 h-48 text-primary"
                // style={{ fill: 'white' }}
              />
            </span>

            {/* Ornament */}
            <div className="mb-8 flex items-center gap-3">
              <div
                className="ft-orn-line h-px flex-1 origin-left"
                style={{ background: GOLD_LIGHT }}
              />
              <div
                className="ft-orn-diamond size-[6px] shrink-0"
                style={{
                  background: GOLD,
                  transform: 'scaleX(2) rotate(45deg) ',
                }}
              />
              <div
                className="ft-orn-line h-px flex-1 origin-right"
                style={{ background: GOLD_LIGHT }}
              />
            </div>

            <p className="mb-3 lg:mb-8 font-sans text-h4 tracking-widest font-extralight">
              Boyden Renovations was formed in 2006 and specializes in
              residential remodeling and renovation. With unmatched
              craftsmanship and customer care we see your project through from
              start to finish leaving you 100% satisfied.
            </p>

            <address
              className="mb-3 lg:mb-8 leading-[2] text-h4 not-italic font-sans font-extralight"
              style={{ color: CHARCOAL }}
            >
              3310 Cabin Rd.
              <br />
              Grove City, OH&nbsp; 43123
            </address>

            <p
              className="mb-0 leading-[1.7] text-h5 uppercase font-sans font-extralight"
              style={{
                color: CHARCOAL,
                letterSpacing: '0.18em',
              }}
            >
              Licensed · Bonded · Insured
            </p>
          </div>
          {/* Navigate */}
          <div
            className="ft-col flex lg:self-start justify-between flex-col lg:justify-normal border-t pt-6 lg:border-t-0 lg:border-l lg:pl-20 lg:pt-0"
            style={{ borderColor: GOLD_LIGHT }}
          >
            <div>
              <p
                className="mb-3 md:mb-8 text-h4 font-medium uppercase"
                style={{
                  color: GOLD,
                  letterSpacing: '0.28em',
                }}
              >
                Navigate
              </p>
              <ul className="m-0 flex list-none flex-col gap-2 md:gap-5 p-0 font-sans font-extralight">
                {NAV_LINKS.map((link) => (
                  <li key={link.to}>
                    <NavLink label={link.label} to={link.to} />
                  </li>
                ))}
              </ul>
            </div>
            {/* Ornament */}
            <div className="flex items-center gap-3 flex-1">
              <div className="justify-center items-center flex w-full lg:pt-[100px] mb-2">
                <ScrollToTopButton />
              </div>
            </div>
          </div>
        </div>
        {/* Placeholder for BR LOGO */}
        {/* <div className="flex justify-center items-center">BR</div> */}
        {/* ── Bottom Bar ── */}
        <div
          className="ft-bottom mx-5 flex flex-col items-start gap-4 border-t py-5 sm:mx-8 sm:flex-row sm:items-center sm:justify-between lg:mx-20"
          style={{ borderColor: GOLD_LIGHT }}
        >
          <span
            className="uppercase font-sans text-h5 font-extralight"
            style={{
              color: CHARCOAL,
              letterSpacing: '0.08em',
            }}
          >
            © {year} Boyden Renovations LLC — All rights reserved.
          </span>

          <div className="flex gap-8 font-sans font-extralight">
            {SOCIALS.map((s) => (
              <SocialLink key={s.label} label={s.label} href={s.href} />
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
